import { func, object, string } from 'prop-types'
import React from 'react'

import { BOOKING_WINDOW } from '../../../constants/Routes'
import AAccordion from '../../../external/components/container/AAccordion'
import Panel from '../../../external/components/container/APanel'
import Cont from '../../../external/components/layout/Cont'
import Section, { SUB } from '../../../external/components/layout/Section'
import ABaseLink from '../../../external/components/link/ABaseLink'
import {
  findObjectInArray,
  htmlBlock,
  isEmpty,
  isUndefined,
} from '../../../external/utilities/GeneralUtils'
import { getPageUIConfig } from '../../../utilities/ConfigUtils'
import { displayBasicFacilityDetails } from '../../../utilities/ContractUtils'
import { hasCoordinates } from '../../../utilities/GeoMapUtils'
import Translation, { simpleTranslate, translate } from '../../Translation'
import FacilityAlerts from '../shared/FacilityAlerts'
import FacilityAmenities from './FacilityAmenities'
import FacilityContactInfo from './FacilityContactInfo'
import FacilityNote from './FacilityNote'

const FacilityDetails = ({
  intl,
  facility,
  contractConfig,
  mainConfig,
  pageId,
}) => {
  const basicDisplay = displayBasicFacilityDetails(contractConfig, facility)

  let notes = null
  if (!isEmpty(facility.description) && !isEmpty(facility.description.notes)) {
    notes = facility.description.notes
  }
  const noteDataMap = {}

  if (!isEmpty(notes)) {
    notes.forEach((note) => {
      noteDataMap[note.type] = note.content
    })
  }

  let coordinatesSection
  if (hasCoordinates(facility.coordinates)) {
    coordinatesSection = (
      <div className="facility-detail-coordinates">
        <span className="bold">
          <Translation intl={intl} mkey="labels_facility_coordinates" />
        </span>
        <p>
          {facility.coordinates.latitude}, {facility.coordinates.longitude}
        </p>
        <p className="spaced">
          <span
            dangerouslySetInnerHTML={htmlBlock(
              facility.coordinates.latitudeDegMinSec,
            )}
          />
          ,{' '}
          <span
            dangerouslySetInnerHTML={htmlBlock(
              facility.coordinates.longitudeDegMinSec,
            )}
          />
        </p>
      </div>
    )
  }

  const directionsSection = isEmpty(noteDataMap.DIRECTIONS_INFO) ? null : (
    <Panel
      header={translate(
        intl,
        'labels_facility_overview_directions',
        null,
        null,
        [facility.contrCode],
        '_Directions',
      )}
      eventKey="DIRECTIONS_INFO"
    >
      <FacilityNote notes={notes} noteType="DIRECTIONS_INFO" intl={intl}>
        {coordinatesSection}
      </FacilityNote>
    </Panel>
  )

  const bookingWindowSection = isEmpty(
    noteDataMap.BOOKING_WINDOW_INFO,
  ) ? null : (
    <Panel
      header={translate(
        intl,
        'labels_facility_overview_booking',
        null,
        null,
        [facility.contrCode],
        '_Booking Window',
      )}
      eventKey={BOOKING_WINDOW}
    >
      <FacilityNote notes={notes} noteType="BOOKING_WINDOW_INFO" intl={intl} />
    </Panel>
  )

  const infoSection = isEmpty(noteDataMap.IMPORTANT_INFO) ? null : (
    <FacilityNote notes={notes} noteType="IMPORTANT_INFO" intl={intl} />
  )

  let locationNotes
  let locationNotesWrapper
  let dailyEntryInfoWrapper
  let additionalInformation
  let additionalInformationWrapper

  if (!isEmpty(facility.relatedLinks)) {
    additionalInformation = facility.relatedLinks.map((linkInfo, key) => (
      <div key={key} className="additional-information-link">
        <ABaseLink to={linkInfo.linkURL} newWin internal={false}>
          {linkInfo.linkLabel}
        </ABaseLink>
      </div>
    ))
    additionalInformationWrapper = (
      <Panel
        header={translate(
          intl,
          'labels_facility_overview_additional_information',
          null,
          null,
          [facility.contrCode],
          '_Additional Information',
        )}
        eventKey="ADDITIONAL_INFO"
      >
        {additionalInformation}
      </Panel>
    )
  }

  if (!isEmpty(facility.locationNotes)) {
    locationNotes = facility.locationNotes.map((note, key) => {
      return isEmpty(note.content) ? null : (
        <p key={key} dangerouslySetInnerHTML={htmlBlock(note.content)} />
      )
    })
    locationNotesWrapper = (
      <div>
        <div className="info-message__item">
          <div className="info-message__content">
            <Translation
              styleClass="info-message__title"
              intl={intl}
              mkey="labels_product_details_notes"
            />
          </div>
        </div>
        {locationNotes}
      </div>
    )
  }

  if (!isEmpty(facility.notes)) {
    const note = findObjectInArray(
      facility.notes,
      'type',
      'DAILYENTRY_IMPORTANT_INFO',
    )
    if (!isEmpty(note) && !isEmpty(note.content)) {
      dailyEntryInfoWrapper = (
        <div>
          <div className="info-message__item">
            <div className="info-message__content">
              <Translation
                styleClass="info-message__title"
                intl={intl}
                mkey="labels_product_details_dailyEntryInfo"
                keyQualifiers={[facility.contrCode]}
                defaultMessage={note.headline}
              />
            </div>
          </div>
          <FacilityNote
            notes={facility.notes}
            noteType="DAILYENTRY_IMPORTANT_INFO"
            intl={intl}
          />
        </div>
      )
    }
  }

  // let facilityFeesSection

  // if (!isEmpty(contractConfig) && contractConfig.productsDisplay.showCampgroundRates) {
  //   facilityFeesSection = (<FacilityFees intl={intl} facility={facility} />)
  // }

  let facilityAlerts

  if (!isEmpty(facility.alerts)) {
    facilityAlerts = <FacilityAlerts intl={intl} facility={facility} />
  }

  if (!basicDisplay) {
    let hasDescriptionPanel = false
    let descriptionPanel
    const descriptionPanelSections = [
      'ORIENTATION_INFO',
      'RECREATION_INFO',
      'PRODUCT_DESCRIPTION',
      'NEARBY_ATTRACTION_INFO',
    ]
    for (const dex in descriptionPanelSections) {
      const note = findObjectInArray(
        notes,
        'type',
        descriptionPanelSections[dex],
      )
      if (!isEmpty(note) && !isEmpty(note.content)) {
        hasDescriptionPanel = true
        break
      }
    }

    if (hasDescriptionPanel) {
      descriptionPanel = (
        <Panel
          header={translate(
            intl,
            'labels_facility_overview_description',
            null,
            null,
            [facility.contrCode],
            '_Description',
          )}
          eventKey="DESCRIPTION"
        >
          <FacilityNote
            notes={notes}
            noteType="ORIENTATION_INFO"
            intl={intl}
            titleKey="labels_facility_overview_natural"
            contractCode={facility.contrCode}
          />
          <FacilityNote
            notes={notes}
            noteType="RECREATION_INFO"
            intl={intl}
            titleKey="labels_facility_overview_recreation"
            contractCode={facility.contrCode}
          />
          <FacilityNote
            notes={notes}
            noteType="PRODUCT_DESCRIPTION"
            intl={intl}
            titleKey="labels_facility_overview_facilities"
            contractCode={facility.contrCode}
          />
          <FacilityNote
            notes={notes}
            noteType="NEARBY_ATTRACTION_INFO"
            intl={intl}
            titleKey="labels_facility_overview_nearby"
            contractCode={facility.contrCode}
          />
        </Panel>
      )
    }

    // const hasBookingWindowSummary = !isEmpty(facility) && !isEmpty(facility.bookingWindowsSummary) && (!isEmpty(facility.bookingWindowsSummary.siteTypes) || !isEmpty(facility.bookingWindowsSummary.bookingWindowDescription))
    // let bookingWindowSummaryWrapper

    // if (hasBookingWindowSummary) {
    //   bookingWindowSummaryWrapper = (
    //     <Panel header={translate(intl, 'labels_facility_overview_booking_window', null, null, [facility.contrCode], '_BookingWindow')}  eventKey="BOOKING_WINDOW_SUMMARY">
    //       <FacilityBookingWindowSummary intl={intl} onSelectProduct={onSelectProduct} facility={facility} />
    //     </Panel>
    //   )
    // }

    let specialEvents
    let specialEventsComment
    let specialEventsWrapper

    if (
      !isEmpty(facility.specialEvents) &&
      !isEmpty(facility.specialEvents.events)
    ) {
      if (!isEmpty(facility.specialEvents.comment)) {
        specialEventsComment = (
          <Cont>
            {simpleTranslate(
              intl,
              facility.specialEvents.comment.token,
              facility.specialEvents.comment.defaultContent,
            )}
            <Section style={SUB} />
          </Cont>
        )
      }
      specialEvents = facility.specialEvents.events.map((event, key) => {
        let section = null
        if (key < facility.specialEvents.events.length - 1) {
          section = <Section style={SUB} />
        }

        return (
          <div key={key}>
            {/* <Cont>
            <TextStyler styleObj={{
              ...DEFAULT_TEXT,
              fontWeight: FONT_WEIGHT.BOLD
            }}>
              {event.name}
            </TextStyler>
          </Cont> */}
            <div dangerouslySetInnerHTML={htmlBlock(event.description)} />
            {section}
          </div>
        )
      })
      specialEventsWrapper = (
        <Panel
          header={simpleTranslate(
            intl,
            facility.specialEvents.title.token,
            facility.specialEvents.title.defaultContent,
          )}
          eventKey="SPECIAL_EVENTS"
        >
          {specialEventsComment}
          {specialEvents}
        </Panel>
      )
    }

    let activeKey = null
    const pageUIconfig = getPageUIConfig(mainConfig, pageId)
    if (
      !isEmpty(pageUIconfig) &&
      !isUndefined(pageUIconfig.facilityDetailsHighlightSectionKey)
    ) {
      activeKey = pageUIconfig.facilityDetailsHighlightSectionKey
    }

    return (
      <div className="facility-overview-details">
        <div>
          <AAccordion defaultActiveKey={activeKey}>
            {descriptionPanel}
            {/* {bookingWindowSummaryWrapper} */}

            <Panel
              header={translate(
                intl,
                'labels_facility_overview_amenities',
                null,
                null,
                [facility.contrCode],
                '_Amenities',
              )}
              eventKey="AMENITIES"
            >
              <FacilityAmenities intl={intl} amenities={facility.amenities} />
            </Panel>
            {/* <Panel header={translate(intl, 'labels_facility_overview_fees', null, null, [facility.contrCode], '_Fees & Cancellations')}  eventKey={FEES}>
              <FacilityNote notes={notes} noteType="FEES_CANCELLATION_WINDOW_INFO" intl={intl} />
              {facilityFeesSection}
            </Panel> */}
            {directionsSection}
            <Panel
              header={translate(
                intl,
                'labels_facility_overview_contact',
                null,
                null,
                [facility.contrCode],
                '_Contact Information',
              )}
              eventKey="CONTACT"
            >
              <FacilityContactInfo
                contactAddress={facility.contactAddress}
                contactPhone={facility.contactPhones}
                contractCode={facility.contrCode}
                intl={intl}
              />
            </Panel>
            {bookingWindowSection}
            {additionalInformationWrapper}
            {specialEventsWrapper}
          </AAccordion>
          <div className="facility-alert">
            {facilityAlerts}
            {infoSection}
            {dailyEntryInfoWrapper}
            {locationNotesWrapper}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="facility-overview-details">
      <FacilityNote
        notes={notes}
        noteType="DIRECTIONS_INFO"
        intl={intl}
        titleKey="labels_facility_overview_directions"
        contractCode={facility.contrCode}
      >
        {coordinatesSection}
      </FacilityNote>
      <FacilityNote
        notes={notes}
        noteType="IMPORTANT_INFO"
        intl={intl}
        titleKey="labels_facility_overview_important_information"
        contractCode={facility.contrCode}
      />
      <FacilityNote
        notes={notes}
        noteType="PRODUCT_DESCRIPTION"
        intl={intl}
        titleKey="labels_facility_overview_facilities"
        contractCode={facility.contrCode}
      />
      <FacilityNote
        notes={notes}
        noteType="RECREATION_INFO"
        intl={intl}
        titleKey="labels_facility_overview_recreation"
        contractCode={facility.contrCode}
      />
      <FacilityNote
        notes={notes}
        noteType="NEARBY_ATTRACTION_INFO"
        intl={intl}
        titleKey="labels_facility_overview_nearby"
        contractCode={facility.contrCode}
      />
      <FacilityContactInfo
        contactAddress={facility.contactAddress}
        contactPhone={facility.contactPhones}
        contractCode={facility.contrCode}
        intl={intl}
      />
    </div>
  )
}

FacilityDetails.propTypes = {
  intl: object.isRequired,
  facility: object.isRequired,
  params: object.isRequired,
  contractConfig: object.isRequired,
  mainConfig: object.isRequired,
  onSelectProduct: func.isRequired,
  pageId: string.isRequired,
}

export default FacilityDetails
